import LocalizedLink from "@/components/LocalizedLink";
import LanguageSelector from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import {
  LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
  getPathLanguage,
  stripLanguagePrefix,
  withLanguageAssetSuffix,
  withLanguagePrefix,
} from "@/lib/i18n";
import { useAuthStore } from "@/store";
import {
  Play,
  ArrowRight,
  CircleCheckBig,
  Menu,
  X,
  WandSparkles,
  Upload,
  Type,
  Download,
  UtensilsCrossed,
  House,
  ShoppingBag,
  Dumbbell,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const HERO_VIDEO_SRC = "/assets/Creavideos-Video-post-instead-of-image.mp4";

const solutionSteps = [
  {
    icon: WandSparkles,
    title: "Choose a template",
  },
  {
    icon: Upload,
    title: "Upload your images or clips",
  },
  {
    icon: Type,
    title: "Customize your text",
  },
  {
    icon: Download,
    title: "Download or share your video",
  },
];

const useCases = [
  {
    icon: UtensilsCrossed,
    title: "Restaurants promoting dishes or menus",
  },
  {
    icon: House,
    title: "Real estate agents showcasing properties",
  },
  {
    icon: ShoppingBag,
    title: "Local shops announcing products and offers",
  },
  {
    icon: Dumbbell,
    title: "Beauty & fitness services",
  },
  {
    icon: CircleCheckBig,
    title: "Any business posting on social media",
  },
];

const pricingPoints = [
  "Plans from 19€/month",
  "Up to 100 videos/month",
  "Cancel anytime",
];

const exampleVideos = [
  {
    title: "AI image to video generation",
    src: "/assets/AI-image-to-video.mp4",
    poster: "/assets/AI-image-to-video.png",
  },
  {
    title: "image with movement/slideshows",
    src: "/assets/Simple-4-slides-template.mp4",
    poster: "/assets/Simple-4-slides-template.png",
  },
  {
    title: "Fun & Creative videos",
    src: "/assets/Thinking-about-your-product-at-work.mp4",
    poster: "/assets/Thinking-about-your-product-at-work.png",
  },
];

const Navbar = () => {
  const { isAuthenticated, user } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <LocalizedLink to="/" className="flex items-center gap-2">
            <img
              src="/assets/logo-creavideos.png"
              alt="CreaVideos"
              className="h-8 w-auto"
            />
          </LocalizedLink>

          <div className="hidden md:flex items-center gap-8">
            <LocalizedLink
              to="/templates"
              className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
            >
              Templates
            </LocalizedLink>
            <LocalizedLink
              to="/pricing"
              className="text-slate-600 hover:text-teal-600 transition-colors font-medium"
            >
              Pricing
            </LocalizedLink>
            <LanguageSelector />
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <LocalizedLink
                  to={user?.role === "admin" ? "/admin" : "/dashboard"}
                >
                  <Button
                    className="btn-primary rounded-full px-6"
                    data-testid="dashboard-btn"
                  >
                    Dashboard
                  </Button>
                </LocalizedLink>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <LocalizedLink to="/login">
                  <Button
                    variant="ghost"
                    className="btn-ghost"
                    data-testid="login-btn"
                  >
                    Log in
                  </Button>
                </LocalizedLink>
              </div>
            )}
          </div>

          <div className="flex items-center md:hidden">
            <LanguageSelector />
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-4">
              <LocalizedLink
                to="/templates"
                className="text-slate-600 font-medium py-2"
              >
                Templates
              </LocalizedLink>
              <LocalizedLink
                to="/pricing"
                className="text-slate-600 font-medium py-2"
              >
                Pricing
              </LocalizedLink>
              {isAuthenticated ? (
                <LocalizedLink
                  to={user?.role === "admin" ? "/admin" : "/dashboard"}
                >
                  <Button className="btn-primary rounded-full w-full">
                    Dashboard
                  </Button>
                </LocalizedLink>
              ) : (
                <div className="flex flex-col gap-2">
                  <LocalizedLink to="/login">
                    <Button variant="outline" className="w-full">
                      Log in
                    </Button>
                  </LocalizedLink>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const ExampleVideoCard = ({
  title,
  src,
  poster,
  ariaLabel,
  className = "aspect-[4/5] w-full object-cover",
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [desktopMode, setDesktopMode] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    const updateDesktopMode = () => setDesktopMode(mediaQuery.matches);
    updateDesktopMode();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateDesktopMode);
      return () => mediaQuery.removeEventListener("change", updateDesktopMode);
    }

    mediaQuery.addListener(updateDesktopMode);
    return () => mediaQuery.removeListener(updateDesktopMode);
  }, []);

  const pauseVideo = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  }, []);

  const playVideo = useCallback(async () => {
    if (!videoRef.current) {
      return;
    }

    try {
      await videoRef.current.play();
    } catch (error) {
      console.debug("Example video playback prevented", error);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || desktopMode || typeof window === "undefined") {
      if (desktopMode) {
        pauseVideo();
      }
      return undefined;
    }

    const syncMobilePlayback = () => {
      if (!containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const visibleHeight =
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      const isVisible = visibleHeight > rect.height * 0.45;

      if (isVisible) {
        playVideo();
      } else {
        pauseVideo();
      }
    };

    syncMobilePlayback();

    const observer = new IntersectionObserver(syncMobilePlayback, {
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });

    observer.observe(container);
    window.addEventListener("scroll", syncMobilePlayback, { passive: true });
    window.addEventListener("resize", syncMobilePlayback);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", syncMobilePlayback);
      window.removeEventListener("resize", syncMobilePlayback);
    };
  }, [desktopMode, pauseVideo, playVideo]);

  return (
    <div className="space-y-4">
      {title && (
        <h3 className="text-xl md:text-2xl font-semibold text-slate-900 text-center">
          {title}
        </h3>
      )}
      <div
        ref={containerRef}
        className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 shadow-xl group"
        onMouseEnter={() => {
          if (desktopMode) {
            setShowControls(true);
            playVideo();
          }
        }}
        onMouseLeave={() => {
          if (desktopMode) {
            setShowControls(false);
            pauseVideo();
          }
        }}
      >
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          muted
          defaultMuted
          playsInline
          preload="metadata"
          loop
          controls={showControls}
          className={className}
          aria-label={ariaLabel || `${title} example video preview`}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onLoadedData={() => {
            if (!desktopMode) {
              playVideo();
            }
          }}
          onCanPlay={() => {
            if (!desktopMode) {
              playVideo();
            }
          }}
          onTouchStart={() => {
            if (!desktopMode) {
              setShowControls(true);
              playVideo();
            }
          }}
          onClick={() => {
            if (!desktopMode) {
              setShowControls(true);
              playVideo();
            }
          }}
        />
        {!isPlaying && !showControls && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center border border-white/30 shadow-lg">
              <Play className="w-6 h-6 fill-white text-white ml-1" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function HomePage() {
  const location = useLocation();
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const currentLanguage = getPathLanguage(location.pathname);
  const localizedHeroVideoSrc = withLanguageAssetSuffix(
    HERO_VIDEO_SRC,
    currentLanguage,
  );
  const localizedExampleVideos = exampleVideos.map((video) => ({
    ...video,
    src: withLanguageAssetSuffix(video.src, currentLanguage),
  }));

  const switchLanguage = (nextLanguage) => {
    const basePath = stripLanguagePrefix(location.pathname);
    const localizedPath = withLanguagePrefix(basePath, nextLanguage);
    window.location.assign(
      `${localizedPath}${location.search}${location.hash}`,
    );
    setLanguageMenuOpen(false);
  };
  const localizedTypesVideoSrc = {
    en: "/assets/Creavideos-5-types-of-Videos-EN.mp4",
    es: "/assets/Creavideos-5-types-of-Videos-ES.mp4",
    fr: "/assets/Creavideos-5-types-of-Videos-FR.mp4",
  }[currentLanguage];
  const euro = (amount) =>
    currentLanguage === "en" ? `€${amount}` : `${amount}€`;
  const pricingCopy = {
    en: {
      firstMonth: `Start your first month for ${euro(9)}`,
      firstMonthOffer: `Start your first month for ${euro(9)} instead of ${euro(19)}`,
      afterFirstMonth: `Plans from ${euro(19)}/month after your first month. Cancel anytime.`,
      enoughToPublish: "That's enough to publish 4 videos every week.",
      perVideo: `Starting from about ${euro(1)} per video.`,
      plansStart: `Plans start at ${euro(19)}/month`,
    },
    fr: {
      firstMonth: `Commencez votre premier mois pour ${euro(9)}`,
      firstMonthOffer: `Commencez votre premier mois pour ${euro(9)} au lieu de ${euro(19)}`,
      afterFirstMonth: `Offres à partir de ${euro(19)}/mois après votre premier mois. Résiliez à tout moment.`,
      enoughToPublish: "C'est suffisant pour publier 4 vidéos par semaine.",
      perVideo: `À partir d'environ ${euro(1)} par vidéo.`,
      plansStart: `Les offres commencent à ${euro(19)}/mois`,
    },
    es: {
      firstMonth: `Empieza tu primer mes por ${euro(9)}`,
      firstMonthOffer: `Empieza tu primer mes por ${euro(9)} en lugar de ${euro(19)}`,
      afterFirstMonth: `Planes desde ${euro(19)}/mes después del primer mes. Cancela cuando quieras.`,
      enoughToPublish: "Es suficiente para publicar 4 videos cada semana.",
      perVideo: `Desde aproximadamente ${euro(1)} por video.`,
      plansStart: `Los planes empiezan en ${euro(19)}/mes`,
    },
  }[currentLanguage];
  const videoTypes = [
    ["AI Image-to-Video", "Turn a photo into a moving video."],
    ["Simple & Visual Templates", "Turn your photos into animated slideshows."],
    [
      "Edited Video Templates",
      "Upload your clips and let CreaVideos do the editing.",
    ],
    ["Fun & Creative Videos", "Create entertaining posts and ads with actors."],
    [
      "Client Review Videos",
      "Collect authentic customer testimonials with a QR code.",
    ],
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <Navbar />

      <section className="pt-24 pb-16 px-4 sm:pt-28 sm:pb-20 md:px-8 bg-grid">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-12 items-center min-w-0">
          <div className="min-w-0 text-center lg:text-left">
            <p className="inline-flex max-w-full rounded-2xl sm:rounded-full bg-teal-50 px-3 sm:px-4 py-2 text-base sm:text-lg leading-snug font-semibold text-teal-700 mb-5 sm:mb-6">
              Videos get more reach than images on social media
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-6xl leading-tight font-bold mb-5 sm:mb-6">
              Your photo posts don&apos;t reach new clients.
            </h1>
            <p className="text-xl sm:text-2xl md:text-3xl leading-snug font-semibold mb-5">
              Turn the photos you already post into videos and reach more people
              without having to create more content.
            </p>
            <p className="text-base sm:text-lg text-slate-600 mb-7 sm:mb-8">
              With CreaVideos, turn your photos into short social media videos
              in about 2 minutes and publish them directly to all your social
              platforms with just one click.
            </p>
            <LocalizedLink to="/pricing">
              <Button
                className="btn-ai h-auto w-full max-w-full whitespace-normal rounded-2xl sm:rounded-full px-4 sm:px-7 py-4 sm:py-5 text-sm sm:text-base md:text-lg leading-snug"
                data-testid="hero-cta"
              >
                <span>{pricingCopy.firstMonthOffer}</span>
                <ArrowRight className="ml-1 sm:ml-2 w-4 sm:w-5 h-4 sm:h-5 shrink-0" />
              </Button>
            </LocalizedLink>
            <p className="mt-4 text-sm text-slate-600">
              {pricingCopy.afterFirstMonth}
            </p>
          </div>
          <div className="min-w-0">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Videos are pushed to people who don&apos;t follow you, while image
                posts are only seen by your existing followers.
              </h2>
            </div>
            <ExampleVideoCard
              src={localizedHeroVideoSrc}
              poster="/assets/Creavideos-Video-post-instead-of-image.png"
              className="block w-full max-w-full aspect-[4/5] object-cover"
              ariaLabel="CreaVideos social media video preview"
            />
          </div>
        </div>
      </section>

      <section className="py-24 px-4 md:px-8 bg-slate-50">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-10">
            You know you need to post videos regularly. But they take time and
            money.
          </h2>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <div className="rounded-3xl bg-white border p-7">
              <p className="text-xl font-semibold mb-3">
                Ideally, you should post 3 or 4 videos every week to reach new
                people.
              </p>
              <p className="text-slate-600">
                But filming and professionally editing that many videos every
                week quickly becomes too expensive for most small businesses.
              </p>
            </div>
            <div className="rounded-3xl bg-teal-700 text-white p-7">
              <p className="font-bold mb-3">So what happens?</p>
              <p className="text-2xl font-bold mb-3">
                You post pictures instead.
              </p>
              <p>
                They&apos;re quick, they&apos;re easy, and you already have
                them.
              </p>
            </div>
          </div>
          <p className="mt-8 text-xl">
            CreaVideos was created to make turning those same pictures into
            videos almost as easy as posting the picture itself.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            Don&apos;t change what you post. Change how you post it.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl bg-slate-50 border p-7">
              <h3 className="text-xl font-bold mb-4">What you do now</h3>
              <p className="text-xl">
                Take a photo → Write a caption → Publish
              </p>
            </div>
            <div className="rounded-3xl bg-cyan-600 text-white p-7">
              <h3 className="text-xl font-bold mb-4">With CreaVideos</h3>
              <p className="text-xl">
                Take the same photo → Choose a template → Edit the text →
                Publish a video
              </p>
            </div>
          </div>
          <div className="text-center max-w-3xl mx-auto mt-10 space-y-4">
            <p className="text-3xl font-bold text-teal-700">
              Just 2 extra minutes.
            </p>
            <p>
              Instead of publishing a photo, you&apos;re publishing a video that
              will be shown to people who don&apos;t follow you.
            </p>
            <p className="text-xl font-bold">
              You&apos;re not creating new content. You&apos;re simply turning
              the content you already create into videos.
            </p>
            <p className="text-3xl font-bold">Same content. More reach.</p>
          </div>
          <div className="mt-12 rounded-3xl bg-cyan-50 p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">
              And there&apos;s another advantage.
            </h3>
            <p className="mb-4">
              Because your content is now a video, you can also publish it on
              TikTok. Many businesses don&apos;t post on TikTok because they
              only create photo posts.
            </p>
            <p className="font-semibold">
              With CreaVideos you can connect to your Instagram, Facebook,
              TikTok and YouTube accounts and share the video on all 4 platforms
              with just one click.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 md:px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Perfect for small businesses
            </h2>
            <p className="text-lg text-slate-600">
              If you post regularly on social media and want an easier, faster
              way to create short videos, CreaVideos is for you. No video
              editing experience needed.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {useCases.map((x) => (
              <div
                key={x.title}
                className="rounded-2xl bg-white border p-5 flex gap-4"
              >
                <x.icon className="w-6 h-6 text-teal-700 shrink-0" />
                <p className="font-semibold">{x.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 xl:gap-14 items-center">
          <div className="text-center lg:text-left">
            <h2 className="text-4xl md:text-5xl font-bold mb-5">
              Don&apos;t post the same kind of video every day
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              CreaVideos gives you different ways to turn your everyday content
              into videos, so your social media stays fresh and varied.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 text-left">
              {videoTypes.map(([title, text], i) => (
                <div
                  key={title}
                  className={`rounded-2xl p-5 ${i % 2 ? "bg-cyan-50" : "bg-teal-50"}`}
                >
                  <h3 className="font-bold mb-2">{title}</h3>
                  <p className="text-sm text-slate-600">{text}</p>
                </div>
              ))}
            </div>
            <p className="text-2xl font-bold mt-8">
              Five types of videos. So your posts never get repetitive.
            </p>
          </div>
          <ExampleVideoCard
            src={localizedTypesVideoSrc}
            className="block aspect-[4/5] w-full object-cover"
            ariaLabel="CreaVideos video types preview"
          />
        </div>
      </section>

      <section className="py-24 px-4 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              See what you can create
            </h2>
            <p className="text-lg text-slate-600">
              Turn your photos into professional-looking social media videos.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {localizedExampleVideos.map((v) => (
              <ExampleVideoCard key={v.title} {...v} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-7">
            Nothing beats a professional video
          </h2>
          <p className="text-xl font-bold mb-5">
            Professionally filmed and edited videos are one of the best ways to
            promote your business on social media.
          </p>
          <p className="text-xl text-teal-700 font-bold mb-8">
            If you have the budget, you should absolutely create them.
          </p>
          <div className="rounded-3xl bg-slate-50 border p-8">
            <h3 className="text-2xl font-bold mb-4">
              CreaVideos isn&apos;t here to replace that.
            </h3>
            <p className="mb-3">
              It&apos;s here for all those other posts where you would normally
              publish a picture.
            </p>
            <p className="font-semibold">
              Instead of photo post, spend two extra minutes and turn it into a
              video that will be shown to non followers.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 md:px-8 bg-gradient-to-br from-teal-700 to-cyan-600 text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-10">
            Post videos regularly without the professional-video budget
          </h2>
          <div className="grid sm:grid-cols-3 gap-4 mb-10">
            <div className="rounded-2xl bg-white/10 p-6">
              <p className="text-3xl font-bold">16</p>
              <p>Create up to 16 videos every month.</p>
            </div>
            <div className="rounded-2xl bg-white/10 p-6">
              <p className="text-3xl font-bold">4</p>
              <p>{pricingCopy.enoughToPublish}</p>
            </div>
            <div className="rounded-2xl bg-white/10 p-6">
              <p className="text-3xl font-bold">{euro(1)}</p>
              <p>{pricingCopy.perVideo}</p>
            </div>
          </div>
          <p className="text-2xl font-bold mb-10">{pricingCopy.plansStart}</p>
          <div className="space-y-2 mb-8">
            <p className="font-bold">
              You&apos;re already creating the content.
            </p>
            <p>You&apos;re already taking the photos.</p>
            <p>You&apos;re already writing the captions.</p>
            <p>Now spend just two extra minutes...</p>
            <p className="text-2xl font-bold">and publish videos instead.</p>
          </div>
          <p className="text-2xl font-bold mb-8">
            So... what&apos;s your excuse for not posting videos more often?
          </p>
          <LocalizedLink
            to="/pricing"
            className="inline-block w-full max-w-full sm:w-auto"
          >
            <Button
              className="h-auto w-full max-w-full whitespace-normal rounded-2xl bg-white px-5 py-4 text-base leading-snug text-teal-700 sm:rounded-full sm:px-8 sm:py-5 sm:text-lg"
              data-testid="cta-signup"
            >
              {pricingCopy.firstMonth}
            </Button>
          </LocalizedLink>
          <p className="mt-4 text-sm">{pricingCopy.afterFirstMonth}</p>
        </div>
      </section>

      <footer className="py-12 px-4 md:px-8 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img
                src="/assets/logo-creavideos.png"
                alt="CreaVideos"
                className="h-8 w-auto opacity-80"
              />
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <LocalizedLink
                to="/templates"
                className="hover:text-white transition-colors"
              >
                Templates
              </LocalizedLink>
              <LocalizedLink
                to="/pricing"
                className="hover:text-white transition-colors"
              >
                Pricing
              </LocalizedLink>
              <LocalizedLink
                to="/terms-of-service"
                className="hover:text-white transition-colors"
              >
                Terms
              </LocalizedLink>
              <LocalizedLink
                to="/license-agreement"
                className="hover:text-white transition-colors"
              >
                License Agreement
              </LocalizedLink>
              <LocalizedLink
                to="/privacy-policy"
                className="hover:text-white transition-colors"
              >
                Privacy
              </LocalizedLink>
              <LocalizedLink
                to="/privacy-policy#data-deletion-instructions"
                className="hover:text-white transition-colors"
              >
                Data Deletion
              </LocalizedLink>
              <a
                href="mailto:support@creavideos.online"
                className="hover:text-white transition-colors"
              >
                Support
              </a>
              <div className="relative">
                <button
                  type="button"
                  className="hover:text-white transition-colors"
                  onClick={() => setLanguageMenuOpen((prev) => !prev)}
                >
                  Language
                </button>
                {languageMenuOpen && (
                  <div className="absolute right-0 bottom-full mb-2 min-w-36 rounded-lg border border-slate-700 bg-slate-950/95 p-2 shadow-lg">
                    {SUPPORTED_LANGUAGES.map((languageCode) => (
                      <button
                        key={languageCode}
                        type="button"
                        className={`block w-full rounded px-3 py-2 text-left text-sm transition-colors ${
                          currentLanguage === languageCode
                            ? "bg-slate-800 text-white"
                            : "text-slate-300 hover:bg-slate-800 hover:text-white"
                        }`}
                        onClick={() => switchLanguage(languageCode)}
                      >
                        {LANGUAGE_NAMES[languageCode]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm">
            © 2025 CreaVideos. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
